/**
 * Opale & Co. — Color Swatches
 * Converts Whisper/Dawn/Spotlight theme color variant buttons → circular swatches
 * Supports single colors, dual-color diagonal splits (e.g. "Red/Black")
 */

(function () {
  'use strict';

  const COLOR_MAP = {
    // Neutrals
    'black':        '#1A1A1A',
    'white':        '#FFFFFF',
    'ivory':        '#FFFFF0',
    'cream':        '#FFF8F0',
    'off white':    '#F8F4EF',
    'beige':        '#F5F0DC',
    'nude':         '#E8C9A0',
    'sand':         '#C2B280',
    'taupe':        '#8B7355',
    'grey':         '#9E9E9E',
    'gray':         '#9E9E9E',
    'light grey':   '#D3D3D3',
    'light gray':   '#D3D3D3',
    'charcoal':     '#36454F',
    'silver':       '#C0C0C0',
    'gold':         '#D4AF37',
    'champagne':    '#F7E7CE',

    // Reds & Pinks
    'red':          '#DC143C',
    'crimson':      '#DC143C',
    'burgundy':     '#800020',
    'wine':         '#722F37',
    'wine red':     '#722F37',
    'maroon':       '#800000',
    'rose':         '#FF007F',
    'blush':        '#F4C2C2',
    'baby pink':    '#F4C2C2',
    'pink':         '#FFB6C1',
    'hot pink':     '#FF69B4',
    'fuchsia':      '#FF00FF',
    'magenta':      '#CC0066',
    'coral':        '#FF6B6B',
    'salmon':       '#FA8072',

    // Oranges & Yellows
    'orange':       '#FF8C00',
    'orange red':   '#FF4500',
    'rust':         '#B7410E',
    'terracotta':   '#E2725B',
    'peach':        '#FFCBA4',
    'yellow':       '#FFD700',
    'mustard':      '#FFDB58',
    'lemon':        '#FFF44F',
    'amber':        '#FFBF00',

    // Greens
    'green':        '#228B22',
    'sage':         '#8FBC8F',
    'olive':        '#6B7C45',
    'mint':         '#98D8C8',
    'emerald':      '#50C878',
    'forest':       '#228B22',
    'lime':         '#32CD32',
    'khaki':        '#BDB76B',

    // Blues
    'blue':         '#0066CC',
    'navy':         '#001F5B',
    'royal blue':   '#4169E1',
    'sky blue':     '#87CEEB',
    'baby blue':    '#89CFF0',
    'cobalt':       '#0047AB',
    'denim':        '#1560BD',
    'turquoise':    '#40E0D0',
    'teal':         '#008080',
    'aqua':         '#00CED1',
    'tiffany blue': '#81D8D0',

    // Purples
    'purple':       '#800080',
    'violet':       '#8B00FF',
    'lavender':     '#C8A8E9',
    'lilac':        '#C8A2C8',
    'mauve':        '#E0B0FF',
    'plum':         '#8E4585',

    // Browns
    'brown':        '#8B4513',
    'tan':          '#D2B48C',
    'caramel':      '#C68642',
    'chocolate':    '#7B3F00',
    'coffee':       '#6F4E37',
    'mocha':        '#967969',
    'cognac':       '#9A4431',
  };

  function resolveColor(name) {
    if (!name) return null;
    const n = name.toLowerCase().trim();
    if (COLOR_MAP[n]) return COLOR_MAP[n];
    for (const [key, val] of Object.entries(COLOR_MAP)) {
      if (n.includes(key) || key.includes(n)) return val;
    }
    return null;
  }

  const DUAL_SEPARATORS = ['/', '&', ' and ', ' + ', '+'];

  function buildBackground(colorName) {
    for (const sep of DUAL_SEPARATORS) {
      if (colorName.includes(sep)) {
        const parts = colorName.split(sep).map(s => s.trim());
        const c1 = resolveColor(parts[0]) || '#CCCCCC';
        const c2 = resolveColor(parts[1]) || '#999999';
        return {
          background: 'linear-gradient(135deg, ' + c1 + ' 50%, ' + c2 + ' 50%)',
          dual: true,
          label: parts[0] + ' / ' + parts[1],
        };
      }
    }
    const hex = resolveColor(colorName);
    return hex ? { background: hex, dual: false, label: colorName } : null;
  }

  function isLight(bg) {
    if (!bg || bg.startsWith('linear') || bg.startsWith('url')) return false;
    const hex = bg.replace('#', '');
    if (hex.length < 6) return false;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 200;
  }

  function injectStyles() {
    if (document.getElementById('opale-swatch-styles')) return;
    const style = document.createElement('style');
    style.id = 'opale-swatch-styles';
    style.textContent = [
      '.opale-swatch {',
      '  position: relative !important;',
      '  display: inline-flex !important;',
      '  align-items: center !important;',
      '  justify-content: center !important;',
      '  width: 34px !important;',
      '  height: 34px !important;',
      '  min-width: 34px !important;',
      '  min-height: 34px !important;',
      '  border-radius: 50% !important;',
      '  border: 2px solid transparent !important;',
      '  box-shadow: 0 0 0 1.5px #D0D0D0;',
      '  cursor: pointer;',
      '  transition: box-shadow 0.18s ease, transform 0.18s ease;',
      '  overflow: hidden !important;',
      '  flex-shrink: 0;',
      '  font-size: 0 !important;',
      '  color: transparent !important;',
      '  padding: 0 !important;',
      '  text-indent: -9999px !important;',
      '  flex-grow: 0 !important;',
      '  background: var(--opale-swatch-bg, #CCCCCC) !important;',
      '}',
      '.opale-swatch--light { box-shadow: 0 0 0 1.5px #BDBDBD; }',
      '.opale-swatch--selected { box-shadow: 0 0 0 2.5px #1A1A1A !important; transform: scale(1.12); }',
      '.opale-swatch:hover:not(.opale-swatch--selected) { box-shadow: 0 0 0 2px #888; transform: scale(1.08); }',
      '.opale-swatch::after {',
      '  content: attr(data-color-name) !important;',
      '  position: absolute;',
      '  bottom: calc(100% + 6px);',
      '  left: 50%;',
      '  transform: translateX(-50%);',
      '  background: #1A1A1A;',
      '  color: #fff !important;',
      '  font-size: 11px !important;',
      '  font-weight: 500;',
      '  padding: 3px 8px;',
      '  border-radius: 4px;',
      '  white-space: nowrap;',
      '  pointer-events: none;',
      '  opacity: 0;',
      '  transition: opacity 0.15s;',
      '  z-index: 100;',
      '  text-indent: 0 !important;',
      '}',
      '.opale-swatch:hover::after { opacity: 1; }',
      '.opale-swatch--disabled { opacity: 0.35; cursor: not-allowed; }',
      /* Override theme grid layout on the fieldset itself */
      'fieldset.opale-color-fieldset {',
      '  display: flex !important;',
      '  flex-wrap: wrap !important;',
      '  gap: 10px !important;',
      '  grid-template-columns: unset !important;',
      '  align-items: center !important;',
      '}',
      '.opale-color-fieldset .variant-option__button-list,',
      '.opale-color-fieldset .block-swatch-list {',
      '  display: flex !important;',
      '  flex-wrap: wrap !important;',
      '  gap: 10px !important;',
      '  list-style: none !important;',
      '  padding: 0 !important;',
      '  margin: 8px 0 0 !important;',
      '}',
      /* Hide the theme's inner pill/text spans so our color shows through */
      '.opale-swatch .variant-option__button-label__pill {',
      '  display: none !important;',
      '}',
      '.opale-swatch .variant-option__button-label__text {',
      '  display: none !important;',
      '}',
      /* Also hide any generic child spans that might overlay */
      '.opale-swatch input[type="radio"] {',
      '  display: none !important;',
      '}',
      /* ── Size Circles ── */
      'fieldset.opale-size-fieldset {',
      '  display: flex !important;',
      '  flex-wrap: wrap !important;',
      '  gap: 10px !important;',
      '  grid-template-columns: unset !important;',
      '  align-items: center !important;',
      '}',
      '.opale-size-swatch {',
      '  position: relative !important;',
      '  display: inline-flex !important;',
      '  align-items: center !important;',
      '  justify-content: center !important;',
      '  width: 44px !important;',
      '  height: 44px !important;',
      '  min-width: 44px !important;',
      '  min-height: 44px !important;',
      '  border-radius: 50% !important;',
      '  overflow: hidden !important;',
      '  padding: 0 !important;',
      '  flex-grow: 0 !important;',
      '  flex-shrink: 0 !important;',
      '  cursor: pointer;',
      '  transition: box-shadow 0.18s ease, transform 0.18s ease;',
      '}',
      '.opale-size-swatch:hover {',
      '  transform: scale(1.08);',
      '}',
      '.opale-size-swatch--disabled {',
      '  opacity: 0.35;',
      '  cursor: not-allowed;',
      '}',
      /* The pill fills and gets clipped to circle */
      '.opale-size-swatch .variant-option__button-label__pill {',
      '  position: absolute !important;',
      '  inset: 0 !important;',
      '  border-radius: 50% !important;',
      '  width: 100% !important;',
      '  height: 100% !important;',
      '}',
      /* Selected size: tan colour matching Add to cart button */
      '.opale-size-swatch:has(input:checked) .variant-option__button-label__pill {',
      '  background: rgb(201, 168, 124) !important;',
      '}',
      '.opale-size-swatch:has(input:checked) .variant-option__button-label__text {',
      '  color: #fff !important;',
      '}',
      /* Text sits on top, centered */
      '.opale-size-swatch .variant-option__button-label__text {',
      '  position: relative !important;',
      '  z-index: 1 !important;',
      '  font-size: 13px !important;',
      '  font-weight: 600 !important;',
      '  letter-spacing: 0.02em !important;',
      '}',
      '.opale-size-swatch input[type="radio"] {',
      '  display: none !important;',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  function loadSavedColors() {
    try {
      const saved = JSON.parse(localStorage.getItem('opale_swatch_colors') || '{}');
      Object.entries(saved).forEach(function(entry) { COLOR_MAP[entry[0]] = entry[1]; });
    } catch (e) {}
  }

  function applySwatches() {
    injectStyles();

    // Whisper theme uses fieldset.variant-option; Dawn uses .product-form__input etc.
    var fieldsets = document.querySelectorAll(
      'fieldset.variant-option, .product-form__input, .product-form fieldset'
    );

    fieldsets.forEach(function(fieldset) {
      var legend = fieldset.querySelector('legend, .form__label, .variant-option__label');
      if (!legend) return;
      var legendText = legend.textContent.toLowerCase().trim();

      var isColor = legendText.includes('color') || legendText.includes('colour') ||
                    legendText.includes('shade') || legendText.includes('print');
      var isSize  = legendText.includes('size') || legendText === 's' || legendText === 'm';

      if (!isColor && !isSize) return;
      if (fieldset.dataset.opaleSwatched) return;
      fieldset.dataset.opaleSwatched = 'true';

      var labels = fieldset.querySelectorAll(
        'label.variant-option__button-label, label.block-swatch__label, label.variant__button-label, label'
      );

      if (isColor) {
        fieldset.classList.add('opale-color-fieldset');

        labels.forEach(function(label) {
          var input = label.querySelector('input');
          var colorName = (input ? input.value : label.textContent).trim();
          if (!colorName) return;

          var bg = buildBackground(colorName);

          label.classList.add('opale-swatch');
          label.setAttribute('data-color-name', bg ? bg.label : colorName);

          if (bg) {
            label.style.setProperty('--opale-swatch-bg', bg.background);
            if (!bg.dual && isLight(bg.background)) {
              label.classList.add('opale-swatch--light');
            }
          } else {
            label.style.setProperty('--opale-swatch-bg', '#CCCCCC');
          }

          if (input) {
            var syncSelected = function() {
              label.classList.toggle('opale-swatch--selected', input.checked);
            };
            syncSelected();
            fieldset.querySelectorAll('input[type="radio"]').forEach(function(r) {
              r.addEventListener('change', syncSelected);
            });
            if (input.disabled) label.classList.add('opale-swatch--disabled');
          }
        });

      } else if (isSize) {
        fieldset.classList.add('opale-size-fieldset');

        labels.forEach(function(label) {
          label.classList.add('opale-size-swatch');
          if (label.querySelector('input')?.disabled) {
            label.classList.add('opale-size-swatch--disabled');
          }
        });
      }
    });
  }

  loadSavedColors();

  function init() {
    applySwatches();
    new MutationObserver(applySwatches)
      .observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
